import { IBalancingStep, IBotData } from '../shared/types';

export const hasBalancingActivity = (family: string, botsData: { [botId: string]: IBotData } | undefined) => {
    if (!botsData) return false;
    
    return Object.entries(botsData)
        .filter(([botId]) => botId.replace(/_\d+$/, '') === family)
        .some(([, botData]) => {
            const balancingUSD = botData.stats.balancingUSD || 0;
            const lentUSD = botData.stats.lentUSD || 0;
            return (balancingUSD + lentUSD) > 0;
        });
};

export const hasLowGasActivity = (family: string, botsData: { [botId: string]: IBotData } | undefined) => {
    if (!botsData) return false;
    
    return Object.entries(botsData)
        .filter(([botId]) => botId.replace(/_\d+$/, '') === family)
        .some(([, botData]) => {
            return botData.stats.isLowGas === true;
        });
};

export const isWaitingForFunds = (stats: IBotData['stats']): boolean => {
    const subStep = stats.currentBalancingSubStep?.toLowerCase() || '';
    const hasExternalFundsText = subStep.includes('waiting for external funds') ||
        subStep.includes('waiting for external') ||
        subStep.includes('external funds') ||
        subStep.includes('external deposit');

    const hasTelegramDepositRequest = Boolean(
        stats.telegramExpectedToken ||
        stats.telegramExpectedAmount ||
        stats.telegramDepositAddress,
    );

    const hasBinanceExternalDepositRequest = Boolean(stats.binanceDepositSubaccount && hasExternalFundsText);

    return hasExternalFundsText || hasTelegramDepositRequest || hasBinanceExternalDepositRequest;
};

export const deriveSwapProgressFromBalancingSteps = (
    stats: IBotData['stats'],
    balancingSteps: IBalancingStep[] | undefined,
): Partial<IBotData['stats']> => {
    if (typeof stats.balancingSwapProgressPct === 'number') return {};

    const subStep = stats.currentBalancingSubStep?.toLowerCase() || '';
    const isSwapStep = subStep.includes('swapping') || subStep.includes('converting');
    if (!isSwapStep || !balancingSteps?.length) return {};

    const pendingStep = balancingSteps
        .filter(step => step.status === 'PENDING')
        .sort((a, b) => (b.date || 0) - (a.date || 0))[0];
    if (!pendingStep) return {};

    const swapTransactions = (pendingStep.transactions || []).filter(tx =>
        tx.action === 'SWAP' &&
        tx.amountIn > 0 &&
        tx.amountOut > 0 &&
        tx.instrumentIn &&
        tx.instrumentIn !== '-' &&
        tx.instrumentOut &&
        tx.instrumentOut !== '-',
    );
    if (swapTransactions.length === 0) return {};

    const tokenIn = swapTransactions[0].instrumentIn;
    const tokenOut = swapTransactions[0].instrumentOut;
    const originalTarget = pendingStep.swapOriginalTarget ?? pendingStep.surplusTokenAmountOrig;
    if (!originalTarget || originalTarget <= 0) return {};

    const convertedAmount = swapTransactions
        .filter(tx => tx.instrumentIn === tokenIn)
        .reduce((sum, tx) => sum + tx.amountIn, 0);
    const purchasedAmount = swapTransactions
        .filter(tx => tx.instrumentOut === tokenOut)
        .reduce((sum, tx) => sum + tx.amountOut, 0);
    const remainingAmount = Math.max(0, originalTarget - convertedAmount);
    const progressPct = Math.max(0, Math.min(100, (convertedAmount / originalTarget) * 100));

    return {
        balancingTokenAmount: stats.balancingTokenAmount ?? pendingStep.surplusTokenAmountOrig,
        balancingSwapOriginalTarget: originalTarget,
        balancingSwapRemaining: remainingAmount,
        balancingSwapPurchased: purchasedAmount,
        balancingSwapTokenIn: tokenIn,
        balancingSwapTokenOut: tokenOut,
        balancingSwapProgressPct: progressPct,
    };
};

export const hasWaitingForFundsActivity = (family: string, botsData: { [botId: string]: IBotData } | undefined) => {
    if (!botsData) return false;
    
    return Object.entries(botsData)
        .filter(([botId]) => botId.replace(/_\d+$/, '') === family)
        .some(([, botData]) => isWaitingForFunds(botData.stats));
};
